import React, { FC, ReactElement, useContext, useEffect, useMemo, useState, useRef } from 'react';
import { AxiosError } from 'axios';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Close,
  CloudUploadOutlined,
  CheckCircleOutline,
  DeleteOutline,
  ErrorOutline,
  FileDownloadOutlined,
  HelpOutlineOutlined,
  RotateRight,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
  Grid2,
} from '@mui/material';
import { FunctionsError } from 'firebase/functions';

import {
  buttonTextIconLarge,
  gray100,
  gray20,
  gray40,
  gray80,
  orange_overlay20,
  primaryButtonBackgroundColor,
  red20,
  red50,
  secondaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
  white,
  white_overlay80,
} from '../../constants/colors';
import {
  boldMd,
  boldXxl,
  regularLg,
  regularMd,
  regularSm,
  regularXl,
  regularXs,
  sideBarWidth,
  spHeaderHeight,
} from '../../constants/location';
import { inputUploadSchema } from '../../validation/input';
import { InputType, type InputUploadType } from '../../validation/input/inputType';

import CustomLoading from '../uiParts/CustomLoading';
import CustomSnackbar from '../uiParts/CustomSnackbar';
import DeleteModal from '../uiParts/DeleteModal';
import FileUploader from '../uiParts/FileUploader';
import SideBar from '../uiParts/SideBar';
import { AuthContext } from '../../contexts/auth';
import { GroupContext } from '../../contexts/group';
import { handleOcrFunctions } from '../../api/firebase/function/handleOcrFunction';
import { buildMinusSearchQuery } from '../../modules/minusSearch';
import {
  collection,
  doc,
  DocumentData,
  DocumentSnapshot,
  getDocs,
  query,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import {
  deleteResult,
  getDailyUploadCount,
  getResultsForTsv,
  getResultsPaginatedOneTime,
  getResultsPaginatedRealtimeFirstPage,
  resultsRef,
  ResultFilter,
  subscribeProcessingCountByGroup,
} from '../../api/firebase/firestore/result';
import { getUser } from '../../api/firebase/firestore/user';
import { GetResultType } from '../../validation/aiocrResult/aiOcrResultType';
import { processingStatus, ocrStatus } from '../../constants/enum';
import InfiniteScroll from 'react-infinite-scroll-component';
import MetaTitle from '../utils/MetaTitle';
import AiOcrSearchForm, { WatchSearchItemsType } from '../uiParts/AiOcrSearchForm';
import dayjs from 'dayjs';
import Papa from 'papaparse';
import Encoding from 'encoding-japanese';
import DescriptionIcon from '@mui/icons-material/Description';
import FileExportDialog from '../uiParts/FileExportDialog';

//Excel出力用
import * as XLSX from 'xlsx';
import { db } from '../../api/firebase/config';

const styles = {
  avatar: {
    height: '24px',
    width: '24px',
  },
  buttonLarge: {
    height: '44px',
    padding: '16px',
    '& .MuiButton-icon': {
      marginRight: '4px',
    },
    '@media screen and (max-width:960px)': {
      width: 'calc(50% - 8px)',
    },
    '@media screen and (max-width:600px)': {
      width: '100%',
    },
  },
  buttonSmall: {
    height: '36px',
    width: '100%',
    '& .MuiButton-icon': {
      marginRight: '4px',
    },
  },
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      width: '480px',
      '@media screen and (max-width:960px)': {
        padding: '32px 16px',
      },
    },
  },
  dialogCloseButton: {
    height: '32px',
    width: '32px',
    '@media screen and (max-width:960px)': {
      height: '24px',
      width: '24px',
    },
  },
  dialogHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
    '@media screen and (max-width:960px)': {
      marginBottom: '16px',
    },
  },
  dialogTitle: {
    ...regularXl,
    '@media screen and (max-width:960px)': {
      ...regularLg,
    },
  },
  header: {
    alignItems: 'center',
    background: white,
    boxSizing: 'border-box',
    display: 'flex',
    height: '80px',
    justifyContent: 'space-between',
    padding: '0 24px',
    width: '100%',
    '@media screen and (max-width:960px)': {
      height: '48px',
      padding: '0 16px',
    },
  },
  headerButtonWrapper: {
    display: 'flex',
    gap: '16px',
    marginLeft: 'auto',
    '@media screen and (max-width:960px)': {
      boxSizing: 'border-box',
      position: 'fixed',
      bottom: 0,
      left: 0,
      backgroundColor: white_overlay80,
      padding: '16px 16px 24px',
      width: '100%',
      zIndex: 1,
    },
    '@media screen and (max-width:600px)': {
      flexDirection: 'column',
      gap: '12px',
    },
  },
  headerTitle: {
    ...boldXxl,
    color: gray100,
    '@media screen and (max-width:960px)': {
      ...boldMd,
      color: gray100,
    },
  },
  icon24: {
    height: '24px',
    marginLeft: '0px',
    width: '24px',
  },
  icon18: {
    height: '18px',
    marginLeft: '0px',
    width: '18px',
  },
  iconButton: {
    padding: 0,
  },
  info: {
    '@media screen and (max-width:780px)': {
      flexDirection: 'column',
      alignContent: 'flex-start',
      gap: '12px',
    },
    bgcolor: red20,
    border: '1px solid' + red50,
    borderRadius: '4px',
    flexDirection: 'row',
    p: '13px 16px',
    width: '100%',
  },
  infoIcon: {
    color: red50,
    mr: '12px',
    width: '22px',
    height: '22px',
  },
  infoText: {
    fontSize: '14px',
    color: gray100,
    textAlign: 'left',
  },
  infoLinkContainer: {
    '@media screen and (max-width:780px)': {
      ml: '35px',
    },
  },
  modalTitle: {},
  pageContent: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginLeft: '240px',
    overflow: 'hidden',
    paddingBottom: '204px',
    width: '100%',
    '@media screen and (max-width:960px)': {
      marginLeft: 0,
      marginTop: spHeaderHeight,
      paddingBottom: '180px',
    },
  },
  pageWrapper: {
    display: 'flex',
  },
  pagination: {
    '& .MuiPagination-ul .Mui-selected': {
      backgroundColor: orange_overlay20,
    },
  },
  paginationWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '40px',
  },
  sidebar: {
    backgroundColor: gray100,
    height: '100vh',
    position: 'fixed',
    width: sideBarWidth,
    '@media screen and (max-width:960px)': {
      height: spHeaderHeight,
      width: '100%',
      zIndex: '100',
    },
  },
  tab: {
    alignItems: 'center',
    backgroundColor: gray40,
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    height: '24px',
    justifyContent: 'center',
    width: '120px',
    '@media screen and (max-width:960px)': {
      width: 'calc(100% / 3)',
    },
  },
  tabActive: {
    backgroundColor: gray100,
  },
  tabText: {
    ...regularXs,
    color: gray100,
  },
  tabTextActive: {
    color: white,
  },
  tabWrapper: {
    display: 'flex',
    gap: '12px',
    margin: '0 16px',
    '@media screen and (max-width:960px)': {
      width: 'calc(100% - 32px)',
    },
  },
  table: {
    minWidth: '960px',
    overflowX: 'auto',
  },
  tableCellAvatar: {
    alignItems: 'center',
    display: 'flex',
    gap: '8px',
    height: '48px',
    padding: '0 16px',
  },
  tableCellButton: {
    width: '70px',
    padding: 0,
  },
  tableDataText: {
    ...regularSm,
    color: gray100,
    padding: '0 16px',
  },
  tableHeadText: {
    ...regularSm,
    color: gray80,
    padding: '0 16px',
    whiteSpace: 'pre-wrap',
  },
  tableHeadRow: {
    height: '48px',
  },
  tableDataRow: {
    height: '48px',
    '&:hover': {
      backgroundColor: gray20,
      cursor: 'pointer',
    },
  },
  tableSection: {
    backgroundColor: white,
    border: '1px solid' + gray40,
    borderRadius: '4px',
    boxSizing: 'border-box',
    margin: '0 16px',
    minHeight: '560px',
    padding: '16px 24px 56px',
    width: 'calc(100% - 32px)',
    '@media screen and (max-width:960px)': {
      border: 'none',
      borderRadius: 0,
      margin: 0,
      width: '100%',
    },
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  dialogButtonWrapper: {
    display: 'flex',
    gap: '16px',
    marginTop: '32px',
    '@media screen and (max-width:960px)': {
      gap: '8px',
      marginTop: '20px',
    },
  },
  uploadCancelButton: {
    ...tertiaryButtonBackgroundColor,
    height: '44px',
    width: 'calc(50% - 8px)',
  },
  uploadCancelButtonText: {
    ...boldMd,
    color: gray80,
  },
  uploadFileChip: {
    margin: '0 8px 8px 0',
  },
  uploadSubmitButton: {
    height: '44px',
    width: 'calc(50% - 8px)',
    ...primaryButtonBackgroundColor,
  },
  uploadSubmitButtonText: {
    ...boldMd,
    color: white,
  },
  uploadText: {
    ...regularMd,
    color: gray80,
  },
  AddressEllipsisText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '224px',
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  roomNumberEllipsisText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '96px',
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  mainEllipsisText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '450px',
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  NameEllipsisText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '136px',
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  Invoice_numberEllipsisText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '88px',
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  showNameEllipsisText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '104px',
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  icon: {
    width: '56px',
    height: '56px',
    margin: '80px 0 24px 0',
    '@media screen and (max-width: 600px)': {
      margin: '24px 0 24px 0',
    },
  },
  title: {
    whiteSpace: 'pre-wrap',
    mb: 2,
    fontWeight: '500',
    color: gray100,
    fontSize: '22px',
    fontStyle: 'normal',
    lineHeight: '20px',
    '@media screen and (max-width: 600px)': {
      marginBottom: '12px',
    },
  },
  describtion: {
    whiteSpace: 'pre-wrap',
    mb: 4,
    ...regularMd,
    '@media screen and (max-width: 600px)': {
      marginBottom: '20px',
    },
  },
  titleAndStatusWrapper: {
    flexGrow: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginRight: '18px',

    '@media screen and (max-width: 960px)': {
      marginRight: '0',
      fontSize: '12px',
    },
  },
  currentMonthUsingStatus: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    fontWeight: '400',
  },
  statusContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '4px',
    color: '#B3B3B3', // default color
    '&[data-status="3"]': {
      // ocrStatus.completed
      color: '#00A33C',
    },
    '&[data-status="99"]': {
      // ocrStatus.error
      color: '#BD0017',
      cursor: 'help',
    },
  },
  statusIcon: {
    fontSize: 22,
  },
};

interface ResultWithInputType extends GetResultType {
  input: InputType;
  showName?: string;
  iconUrl?: string;
  fileName?: string;
  ocr_status?: number;
  error_message?: string;
  error_acknowledged?: boolean;
}

type resultIds = {
  prevResultId: string | null;
  nextResultId: string | null;
};

const AiOcr = (): ReactElement => {
  const { groupId } = useParams<{ groupId?: string }>();
  const { groups, isLoadingGroups } = useContext(GroupContext);
  const { user, isLoading } = useContext(AuthContext);

  // クエリ用のフィルタを計算
  // 個人画面: 自分の全レコード（グループ機能前のOCR含む） + 所属グループのレコード
  // グループ画面: 特定グループの結果のみ
  // NOTE: groupsはGroupContext内のuseStateで管理されており、refreshGroups()呼び出し時のみ参照が変わる
  const resultFilter: ResultFilter = useMemo(() => {
    if (groupId !== undefined) return groupId;
    return {
      createdBy: user?.uid ?? '',
      groupIds: groups.map(g => g.groupId),
    };
  }, [groupId, groups, user?.uid]);

  const [loading, setLoading] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<number>(processingStatus.processing);
  const [uploadModalOpen, setUploadModalOpen] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string>('');
  const [deleteTargetIndex, setDeleteTargetIndex] = useState<number | null>(null);
  const navigate = useNavigate();
  const [fileExportModalOpen, setFileExportModalOpen] = useState<boolean>(false); //ファイル出力モーダル
  const [isUploading, setIsUploading] = useState<boolean>(false);

  // 日次アップロード上限
  const [dailyLimit, setDailyLimit] = useState<number | null>(null);
  const [remainingUploads, setRemainingUploads] = useState<number | null>(null);

  // Perplexityクォータ超過エラーバナー
  const [perplexityQuotaError, setPerplexityQuotaError] = useState<boolean>(false);

  // 処理中件数（リアルタイム）
  const [processingCount, setProcessingCount] = useState<number>(0);

  // データ取得関連
  const resultLimit = 50;
  const [showData, setShowData] = useState<ResultWithInputType[]>([]);
  const [lastVisible, setLastVisible] = useState<DocumentSnapshot | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [showDataFlg, setShowDataFlg] = useState<boolean | null>(null);

  // 検索機能
  const [watchSearchItems, setWatchSearchItems] = useState<WatchSearchItemsType>({
    debtorName: null,
    mansionName: null,
    establishmentStartDate: null,
    establishmentEndDate: null,
    responsiblePerson: '',
    startDate: null,
    endDate: null,
  });

  // 日次アップロード制限の取得
  const fetchDailyLimitInfo = async (): Promise<void> => {
    if (!user?.uid) return;
    try {
      const [userData, count] = await Promise.all([
        getUser(user.uid),
        getDailyUploadCount(user.uid),
      ]);
      const limit = userData?.daily_limit ?? null;
      setDailyLimit(limit);
      if (limit != null) {
        setRemainingUploads(Math.max(0, limit - count));
      } else {
        setRemainingUploads(null);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('日次アップロード制限の取得に失敗しました:', error);
    }
  };

  useEffect(() => {
    fetchDailyLimitInfo();
  }, [user]);

  //リアルタイムリスナ
  const realtimeUnsubRef = useRef<(() => void) | null>(null);

  // ユーザー情報キャッシュ（スクロールのたびに同じユーザーを再取得しない）
  const userCacheRef = useRef<
    Record<string, { name: string; iconUrl?: string; registerStatus?: number }>
  >({});

  //リアルタイムリスナの登録関数
  const subscribeRealtime = (): void => {
    //既存のリスナがあれば解除
    if (realtimeUnsubRef.current) {
      realtimeUnsubRef.current();
    }
    realtimeUnsubRef.current = getResultsPaginatedRealtimeFirstPage(
      selectedTab,
      resultLimit,
      async (rawResults, newLast, isLastPage) => {
        const updated = await mergeUserInfoToResults(rawResults, selectedTab);
        setShowData(updated);
        setLastVisible(newLast);
        setHasMore(!isLastPage);
        setShowDataFlg(rawResults.length > 0);

        // Perplexityクォータ超過エラーの検出
        const hasPerplexityError = rawResults.some(
          r => r.error_message === 'perplexity_quota_exceeded' && !r.error_acknowledged,
        );
        setPerplexityQuotaError(hasPerplexityError);

        if (!isUploading && loading) {
          // アップロード中でなく、かつ現在ローディング中の場合のみローディングを非表示にする
          setLoading(false);
        }
      },
      watchSearchItems,
      resultFilter,
    );
  };

  const mergeUserInfoToResults = async (
    rawResults: DocumentData[],
    status: number,
  ): Promise<ResultWithInputType[]> => {
    // userIDを一意に取得
    const userIds = Array.from(
      new Set(
        rawResults
          .map(item => {
            if (status === processingStatus.draft || status === processingStatus.processing) {
              return item.created_by;
            } else {
              return item.updated_by;
            }
          })
          .filter((id): id is string => !!id),
      ),
    );

    // キャッシュにないユーザーIDだけ取得
    const uncachedUserIds = userIds.filter(id => !userCacheRef.current[id]);

    if (uncachedUserIds.length > 0) {
      const usersRef = collection(db, 'users');
      for (let i = 0; i < uncachedUserIds.length; i += 10) {
        const chunk = uncachedUserIds.slice(i, i + 10);
        const q = query(usersRef, where('__name__', 'in', chunk));
        const userSnap = await getDocs(q);
        userSnap.docs.forEach(docSnap => {
          const d = docSnap.data();
          userCacheRef.current[docSnap.id] = {
            name: d.name,
            iconUrl: d.iconUrl,
            registerStatus: d.registerStatus,
          };
        });
      }
    }

    // ResultWithInputType[] に変換（キャッシュから参照）
    const updatedResults: ResultWithInputType[] = rawResults.map(item => {
      const userId = status === processingStatus.processing ? item.created_by : item.updated_by;
      // inputからファイル名を取得
      const fileName = item.input?.original_files?.[0]?.file_name || null;
      const cachedUser = userId ? userCacheRef.current[userId] : undefined;

      if (!cachedUser) {
        return {
          ...item,
          fileName: fileName,
        } as ResultWithInputType;
      }
      return {
        ...item,
        showName: cachedUser.name,
        iconUrl: cachedUser.iconUrl,
        fileName: fileName,
      } as ResultWithInputType;
    });

    return updatedResults;
  };

  useEffect(() => {
    // 個人画面ではグループ情報とユーザー情報の読み込み完了を待つ
    if (groupId === undefined && (isLoadingGroups || isLoading)) return;

    subscribeRealtime();

    return () => {
      if (realtimeUnsubRef.current) {
        realtimeUnsubRef.current();
        realtimeUnsubRef.current = null;
      }
    };
  }, [selectedTab, watchSearchItems, resultFilter, isLoadingGroups, isLoading]);

  // 処理中件数のリアルタイム監視（グループ画面のみ）
  useEffect(() => {
    if (!groupId) return;
    const unsub = subscribeProcessingCountByGroup(groupId, count => setProcessingCount(count));
    return unsub;
  }, [groupId]);

  // スクロールで追加データ取得
  const fetchMoreData = async (): Promise<void> => {
    if (!loading && hasMore) {
      setLoading(true);
      try {
        const { results, newLastVisible, isLastPage } = await getResultsPaginatedOneTime(
          selectedTab,
          resultLimit,
          lastVisible,
          watchSearchItems,
          resultFilter,
        );

        const updatedResults = await mergeUserInfoToResults(results, selectedTab);

        setShowData(prev => [...prev, ...updatedResults]);
        setLastVisible(newLastVisible);
        setHasMore(!isLastPage);
        setShowDataFlg(results.length > 0);
      } catch (error) {
        setSnackbarOpen(true);
        setErrorText('データの取得に失敗しました。');
      } finally {
        setLoading(false);
      }
    }
  };

  // ファイルアップロード
  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    watch,
    reset,
    trigger,
  } = useForm<InputUploadType>({
    resolver: yupResolver(inputUploadSchema),
  });

  // アップロードファイル選択時
  const handleFilesSelected = (files: FileList | null, onChange: (value: File[]) => void): void => {
    if (files) {
      const fileArray = Array.from(files);
      const updatedFiles = watch('images') ? [...watch('images'), ...fileArray] : fileArray;
      onChange(updatedFiles);
    }
  };

  // アップロードファイル削除時
  const handleFilesDeleted = (index: number): void => {
    const updatedImages = watch('images').filter((_, filterIndex) => filterIndex !== index);
    setValue('images', updatedImages);
    trigger('images');
  };

  // アップロード実行ボタン押下時
  const [infoText, setInfoText] = useState<string>('');
  const onSubmit = async (): Promise<void> => {
    setLoading(true);
    setIsUploading(true); // アップロード開始時にtrueに設定
    try {
      if (!user) throw new Error('ログインしてください。');

      const filesToProcess = watch('images');
      if (!filesToProcess || filesToProcess.length === 0) {
        setErrorText('アップロードするファイルがありません。');
        setInfoText('');
        setSnackbarOpen(true);
        setLoading(false);
        setIsUploading(false); // エラー時もfalseに設定
        return;
      }

      // 日次アップロード上限チェック
      if (dailyLimit != null) {
        const currentCount = await getDailyUploadCount(user.uid);
        if (currentCount + filesToProcess.length > dailyLimit) {
          const remaining = Math.max(0, dailyLimit - currentCount);
          setErrorText(
            `1日のアップロード上限（${dailyLimit}件）に達しています。本日の残り: ${remaining}件`,
          );
          setInfoText('');
          setSnackbarOpen(true);
          setLoading(false);
          setIsUploading(false);
          setRemainingUploads(remaining);
          return;
        }
      }

      await handleOcrFunctions(filesToProcess, user.uid, groupId ?? null);

      reset({ images: [] });
      setFileCountOverText('');
      setErrorText('');
      setInfoText(
        `${filesToProcess.length}件のAI文字読み取り処理を開始しました。完了までしばらくお待ちください。`,
      );
      setUploadModalOpen(false);
      setLoading(false);
      setSnackbarOpen(true);
      setIsUploading(false); // 成功時もfalseに設定

      // アップロード成功後に残数を更新
      await fetchDailyLimitInfo();
    } catch (error: unknown) {
      let errorText: string;
      // エラー時の処理（必要に応じて）
      if (error instanceof FunctionsError) {
        // Firebase Functions からのエラーメッセージをそのまま使用
        errorText = error.message;
      } else if (error instanceof AxiosError && error.response) {
        // Axios エラーの場合（元のコードを保持）
        errorText = error.response.data.message;
      } else if (error instanceof Error) {
        // Error インスタンスの場合
        errorText = error.message;
      } else {
        // その他のエラーの場合
        errorText = '予期せぬエラーが発生しました。';
      }
      setInfoText('');
      setErrorText(errorText);
      setUploadModalOpen(false);
      setLoading(false);
      setSnackbarOpen(true);
      setIsUploading(false); // エラー時もfalseに設定
    }
  };

  // タブコンポーネント
  const TabItem: FC<{ status: number }> = ({ status }) => {
    const onClick: () => void = () => {
      if (selectedTab === status) return;
      setLoading(true);
      setShowData([]);
      setShowDataFlg(null);
      setLastVisible(null);
      setSelectedTab(status);
    };

    const statusLabels = {
      [processingStatus.processing]: '未処理',
      [processingStatus.completed]: '処理済',
      [processingStatus.outputted]: '出力済',
    };
    const labelText = statusLabels[status];

    return (
      <Box sx={[styles.tab, selectedTab === status && styles.tabActive]} onClick={onClick}>
        <Typography sx={[styles.tabText, selectedTab === status && styles.tabTextActive]}>
          {labelText}
        </Typography>
      </Box>
    );
  };

  // タイムスタンプ変換
  const formatTimestamp = (inputTimestamp: Timestamp): string => {
    const targetSeconds = inputTimestamp.seconds;
    return dayjs(targetSeconds * 1000).format('YYYY/MM/DD HH:mm');
  };

  // 削除対象前後のresultIdを取得する
  const getResultIds = (showData: { resultId: string }[]): resultIds => {
    if (deleteTargetIndex === null) {
      return { prevResultId: null, nextResultId: null };
    }

    const prevResultId = deleteTargetIndex > 0 ? showData[deleteTargetIndex - 1].resultId : null;

    const nextResultId =
      deleteTargetIndex + 1 < showData.length ? showData[deleteTargetIndex + 1].resultId : null;

    return { prevResultId, nextResultId };
  };

  // Perplexityクォータ超過エラーを一括確認済みにする
  const handleAcknowledgePerplexityError = async (): Promise<void> => {
    try {
      const errorResults = showData.filter(
        r => r.error_message === 'perplexity_quota_exceeded' && !r.error_acknowledged,
      );
      if (errorResults.length === 0) return;

      const batch = writeBatch(db);
      errorResults.forEach(r => {
        const docRef = doc(resultsRef, r.resultId);
        batch.update(docRef, { error_acknowledged: true });
      });
      await batch.commit();
      setPerplexityQuotaError(false);
    } catch {
      setErrorText('エラーの確認済み処理に失敗しました。');
      setSnackbarOpen(true);
    }
  };

  const handleDeleteResult = async (): Promise<void> => {
    try {
      const { prevResultId, nextResultId } = getResultIds(showData);
      await deleteResult(deleteTargetId);
      setDeleteModalOpen(false);
      const serializedSearchData = {
        ...watchSearchItems,
        startDate: watchSearchItems.startDate ? watchSearchItems.startDate.toISOString() : null,
        endDate: watchSearchItems.endDate ? watchSearchItems.endDate.toISOString() : null,
      };
      const deleteCompletePath = groupId
        ? `/groups/${groupId}/ai-ocr/delete/complete`
        : '/ai-ocr/delete/complete';
      navigate(deleteCompletePath, {
        state: { prevId: prevResultId, nextId: nextResultId, searchData: serializedSearchData },
      });
    } catch (e) {
      setInfoText('');
      setErrorText('削除に失敗しました。');
      setSnackbarOpen(true);
    }
  };

  useEffect(() => {
    if (isLoading) return;
    // 未ログイン状態の場合はログイン画面へ遷移
    if (user === null) navigate('/');
  }, [user, isLoading]);

  const exportHeaders = [
    '物件のマンション名',
    '物件の部屋番号',
    '物件の住所',
    '所有者(債務者)の氏名',
    '原因の設定年月日',
    '所有者(債務者)の住所',
    '抵当権者の会社名',
    '借入額',
    '金利',
    '前所有者法人名（分譲会社・販売会社）',
    '勤務先候補①',
    '勤務先候補②',
    'マイナス検索文字列',
    '作成日',
    '作成者',
  ];

  // CSV出力
  //xlsx, csv(DOM付きUTF-8), csv(s-jis)で出力
  const handleDownload = async (
    fileType: 'excel' | 'csv',
    encoding: 'utf-8' | 'shift-jis',
  ): Promise<void> => {
    setLoading(true);
    try {
      // 結果データ取得
      const resultData = await getResultsForTsv(selectedTab, watchSearchItems, resultFilter);

      // ユーザー情報を一括取得
      const userIds = Array.from(
        new Set(resultData.map(item => item.created_by).filter((id): id is string => !!id)),
      );

      const userMap: Record<string, { name: string }> = {};

      if (userIds.length > 0) {
        for (let i = 0; i < userIds.length; i += 10) {
          const chunk = userIds.slice(i, i + 10);
          const q = query(collection(db, 'users'), where('__name__', 'in', chunk));
          const userSnap = await getDocs(q);
          userSnap.docs.forEach(docSnap => {
            const d = docSnap.data();
            userMap[docSnap.id] = {
              name: d.name,
            };
          });
        }
      }

      const rows = resultData
        .filter(data => !data.is_corporate_owner)
        .map(data => {
          //日付の変換
          const createdAt = new Date(
            data.created_at.seconds * 1000 + data.created_at.nanoseconds / 1000000,
          );

          const baseRow = {
            物件のマンション名: data.result_data?.mansionName?.value || '-',
            物件の部屋番号: data.result_data?.roomNumber?.value || '-',
            物件の住所: data.result_data?.propertyAddress?.value || '-',
            '所有者(債務者)の氏名': data.result_data?.debtorName?.value || '-',
            原因の設定年月日: data.result_data?.establishmentDate?.value || '-',
            '所有者(債務者)の住所': data.result_data?.debtorAddress?.value || '-',
            抵当権者の会社名: data.result_data?.mortgageeCompanyName?.value || '-',
            借入額: data.result_data?.loanAmount?.value || '-',
            金利: data.result_data?.interestRate?.value || '-',
            '前所有者法人名（分譲会社・販売会社）':
              data.result_data?.previousOwnerCorporateName?.value || '-',
            '勤務先候補①': data.result_data?.candidateCompaniesByGemini?.value || '-',
            '勤務先候補②': data.result_data?.candidateCompanies?.value || '-',
            マイナス検索文字列:
              buildMinusSearchQuery({
                candidateCompaniesByGemini: data.result_data?.candidateCompaniesByGemini?.value,
                candidateCompanies: data.result_data?.candidateCompanies?.value,
                debtorName: data.result_data?.debtorName?.value,
                debtorAddress: data.result_data?.debtorAddress?.value,
              }) || '-',
            作成日: dayjs(createdAt).format('YYYY/MM/DD HH:mm'),
            作成者: userMap[data.created_by]?.name ?? '-',
          };
          return baseRow;
        });

      // ファイル名を設定
      const statusLabels = {
        [processingStatus.processing]: '下書き_未処理',
        [processingStatus.completed]: '処理済',
        [processingStatus.outputted]: '出力済',
      };

      const baseName = `AI文字読取一覧_${statusLabels[selectedTab]}_${dayjs().format('YYYYMMDDHHmmss')}`;

      if (fileType === 'excel') {
        // --- Excel出力 (.xlsx) ---
        const ws = XLSX.utils.json_to_sheet([], { header: exportHeaders });
        XLSX.utils.sheet_add_aoa(ws, [exportHeaders], { origin: 'A1' });
        XLSX.utils.sheet_add_json(ws, rows, {
          skipHeader: true,
          origin: 'A2',
        });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([wbout], { type: 'application/octet-stream' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${baseName}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // --- CSV出力 (.csv) ---
        const csv = Papa.unparse({
          fields: exportHeaders,
          data: rows,
        });

        let blob: Blob;
        if (encoding === 'utf-8') {
          // BOM付き UTF-8
          const bom = '\uFEFF';
          blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' });
        } else {
          // Shift-JIS
          const sjisArray = Encoding.convert(Encoding.stringToCode(csv), 'SJIS', 'UNICODE');
          blob = new Blob([new Uint8Array(sjisArray)], {
            type: 'text/csv;charset=shift_jis',
          });
        }

        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${baseName}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      // --- 出力済ステータス更新 + 再読み込み ---
      const updatePromises = resultData.map(data => {
        const docRef = doc(resultsRef, data.id);
        return updateDoc(docRef, { processing_status: processingStatus.outputted });
      });
      await Promise.all(updatePromises);
      //リアルタイムリスナを解除
      if (realtimeUnsubRef.current) {
        realtimeUnsubRef.current();
        realtimeUnsubRef.current = null;
      }

      //状態を更新
      const {
        results: oneTimeResults,
        newLastVisible: oneTimeLast,
        isLastPage: oneTimeIsLast,
      } = await getResultsPaginatedOneTime(
        selectedTab,
        resultLimit,
        null,
        watchSearchItems,
        resultFilter,
      );

      //マージ後に画面更新
      const merged = await mergeUserInfoToResults(oneTimeResults, selectedTab);
      setShowData(merged);
      setLastVisible(oneTimeLast);
      setHasMore(!oneTimeIsLast);
      setShowDataFlg(oneTimeResults.length > 0);
      setInfoText('出力済タブに移行しました。');

      //リアルタイムリスナを起動
      subscribeRealtime();
    } catch (error) {
      setErrorText('ファイルのダウンロードに失敗しました。');
    } finally {
      setLoading(false);
      setSnackbarOpen(true);
    }
  };

  // データが0件の時のtitleの見出しテキスト
  const titleLabel = ((): string => {
    switch (selectedTab) {
      case processingStatus.completed:
        return '処理済ファイルがありません';
      case processingStatus.outputted:
        return '出力済ファイルがありません';
      case processingStatus.processing:
      default:
        return '未処理ファイルがありません';
    }
  })();

  // データが0件の時のdescriptionの見出しテキスト
  const desctiptionLabel = ((): string => {
    switch (selectedTab) {
      case processingStatus.completed:
        return '未処理ファイルを処理すると処理済タブに移行されます。\nAI文字読取結果画面から「処理済みにする」を押下してください。';
      case processingStatus.outputted:
        return '未処理・処理済ファイルを出力すると出力済タブに移行されます。\n右上の「ファイル出力」を押下して実行してください。';
      case processingStatus.processing:
      default:
        return 'AI文字読取が完了すると未処理タブに表示されます。\n右上の「ファイルをアップロード」ボタンからAI文字読取を実行してください。';
    }
  })();

  const [fileCountOverText, setFileCountOverText] = useState<string>('');

  return (
    <Box sx={styles.pageWrapper}>
      <MetaTitle />
      <Box sx={styles.sidebar}>
        <SideBar />
      </Box>

      <Box sx={styles.pageContent}>
        <Box sx={styles.header}>
          <Box sx={styles.titleAndStatusWrapper}>
            <Typography sx={styles.headerTitle}>
              {groupId
                ? `${groups.find(g => g.groupId === groupId)?.name ?? ''} - AI文字読取`
                : 'AI文字読取'}
            </Typography>
          </Box>
          <Box sx={styles.headerButtonWrapper}>
            {(groupId || groups.length === 0) && (
              <>
                {dailyLimit != null && (
                  <Typography
                    sx={{
                      fontSize: '13px',
                      color: remainingUploads === 0 ? red50 : gray80,
                      whiteSpace: 'nowrap',
                      mr: '8px',
                      alignSelf: 'center',
                    }}
                  >
                    {remainingUploads === 0
                      ? '本日の上限に達しました'
                      : `本日の残り: ${remainingUploads}/${dailyLimit}件`}
                  </Typography>
                )}
                <Button
                  onClick={(): void => setUploadModalOpen(true)}
                  startIcon={<CloudUploadOutlined sx={styles.icon24} />}
                  sx={[primaryButtonBackgroundColor, styles.buttonLarge]}
                  disabled={dailyLimit != null && remainingUploads === 0}
                >
                  <Typography sx={buttonTextIconLarge}>ファイルをアップロード</Typography>
                </Button>
              </>
            )}
            <Button
              startIcon={<FileDownloadOutlined sx={styles.icon24} />}
              sx={[secondaryButtonBackgroundColor, styles.buttonLarge]}
              onClick={(): void => setFileExportModalOpen(true)}
            >
              <Typography sx={buttonTextIconLarge}>ファイル出力</Typography>
            </Button>
          </Box>
        </Box>

        <Grid2 container flexDirection={'column'} gap={'16px'} width={'100%'}>
          <Box sx={{ marginLeft: '16px', marginRight: '16px', width: 'calc(100% - 32px)' }}>
            <AiOcrSearchForm
              watchSearchItems={watchSearchItems}
              setWatchSearchItems={setWatchSearchItems}
              groupId={groupId}
            />
          </Box>
          <Box sx={styles.tabWrapper}>
            <TabItem status={processingStatus.processing} />
            <TabItem status={processingStatus.completed} />
            <TabItem status={processingStatus.outputted} />
          </Box>

          {/* 処理中件数バナー（グループ画面のみ） */}
          {groupId && (
            <Box
              sx={{
                margin: '8px 16px 0',
                padding: '8px 16px',
                borderRadius: '6px',
                backgroundColor: processingCount > 0 ? '#FFF3E0' : '#E8F5E9',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Box
                sx={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: processingCount > 0 ? '#FF9800' : '#4CAF50',
                  flexShrink: 0,
                  ...(processingCount > 0 && {
                    animation: 'pulse 1.5s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.3 },
                    },
                  }),
                }}
              />
              <Typography
                sx={{ fontSize: '13px', color: processingCount > 0 ? '#E65100' : '#2E7D32' }}
              >
                {processingCount > 0
                  ? `読み取り中 ${processingCount}件`
                  : '全件の読み取りが完了しています'}
              </Typography>
            </Box>
          )}

          {/* Perplexityクォータ超過エラーバナー */}
          {perplexityQuotaError && (
            <Box
              sx={{
                ...styles.info,
                margin: '0 16px',
                width: 'calc(100% - 32px)',
                boxSizing: 'border-box',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <ErrorOutline sx={styles.infoIcon} />
                  <Typography sx={styles.infoText}>
                    外部APIのクレジット残高が不足しています。管理者にお問い合わせください。
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: gray100,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    ml: '16px',
                    textDecoration: 'underline',
                  }}
                  onClick={handleAcknowledgePerplexityError}
                >
                  了解しました
                </Typography>
              </Box>
            </Box>
          )}

          <Box sx={styles.tableSection}>
            {showDataFlg === null ? (
              <CustomLoading loading={true} />
            ) : showDataFlg ? (
              <Box sx={styles.tableWrapper}>
                <InfiniteScroll
                  dataLength={showData.length}
                  next={fetchMoreData}
                  hasMore={hasMore}
                  loader={<CustomLoading loading={loading} />}
                  scrollThreshold="200px"
                >
                  <Table sx={styles.table}>
                    <TableHead>
                      <TableRow sx={styles.tableHeadRow}>
                        <TableCell sx={styles.tableHeadText}>{`アップ\nロード日`}</TableCell>
                        <TableCell sx={styles.tableHeadText}>読取ステータス</TableCell>
                        <TableCell sx={styles.tableHeadText}>ファイル名</TableCell>
                        <TableCell sx={styles.tableHeadText}>マンション名</TableCell>
                        <TableCell sx={styles.tableHeadText}>{'部屋\n番号'}</TableCell>
                        <TableCell sx={styles.tableHeadText}>{'原因の\n設定年月日'}</TableCell>
                        <TableCell sx={styles.tableHeadText}>所有者(債務者)の住所</TableCell>
                        <TableCell sx={styles.tableHeadText}>所有者(債務者)の氏名</TableCell>
                        <TableCell sx={styles.tableHeadText}>抵当権者の会社名</TableCell>
                        <TableCell sx={{ ...styles.tableHeadText, whiteSpace: 'nowrap' }}>
                          グループ
                        </TableCell>
                        <TableCell sx={styles.tableHeadText}>作成者</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {showData.map((data, index) => (
                        <TableRow
                          key={index}
                          onClick={(): void => {
                            if (
                              data.ocr_status === ocrStatus.processing ||
                              data.ocr_status === ocrStatus.error
                            ) {
                              return; // クリックを無効化
                            }
                            const serializedSearchData = {
                              ...watchSearchItems,
                              startDate: watchSearchItems.startDate
                                ? watchSearchItems.startDate.toISOString()
                                : null,
                              endDate: watchSearchItems.endDate
                                ? watchSearchItems.endDate.toISOString()
                                : null,
                            };
                            const resultPath = groupId
                              ? `/groups/${groupId}/ai-ocr/result/${data.resultId}`
                              : `/ai-ocr/result/${data.resultId}`;
                            navigate(resultPath, {
                              state: { searchData: serializedSearchData },
                            });
                          }}
                          sx={[
                            styles.tableDataRow,
                            ...(data.is_corporate_owner
                              ? [
                                  {
                                    backgroundColor: '#eeeeee',
                                    '&:hover': { backgroundColor: '#e8e8e8' },
                                    '& .MuiTableCell-root': { color: '#bdbdbd' },
                                  },
                                ]
                              : []),
                            ...(data.ocr_status === ocrStatus.processing ||
                            data.ocr_status === ocrStatus.error
                              ? [
                                  {
                                    cursor: 'not-allowed',
                                    '&:hover': {
                                      backgroundColor: 'white',
                                      cursor: 'unset',
                                    },
                                  },
                                ]
                              : []),
                          ]}
                        >
                          {/* アップロード日 */}
                          <TableCell sx={styles.tableDataText}>
                            {formatTimestamp(data.created_at)}
                          </TableCell>
                          {/* 進捗ステータス */}
                          <TableCell sx={styles.tableDataText}>
                            {/* エラー状態の場合のみInfoアイコンを表示 */}
                            {data.ocr_status === ocrStatus.error ? (
                              <Tooltip
                                title={
                                  <>
                                    ファイルの読み取りに失敗しました。
                                    <br />
                                    再度アップロードをお願いします。
                                  </>
                                }
                              >
                                <Box sx={styles.statusContainer} data-status={data.ocr_status}>
                                  <HelpOutlineOutlined />
                                  <span style={styles.mainEllipsisText}>読取エラー</span>
                                </Box>
                              </Tooltip>
                            ) : (
                              <Box sx={styles.statusContainer} data-status={data.ocr_status}>
                                {((): JSX.Element | string => {
                                  switch (data.ocr_status) {
                                    case ocrStatus.uploaded:
                                    case ocrStatus.processing:
                                      return <RotateRight sx={styles.statusIcon} />;
                                    case ocrStatus.completed:
                                      return <CheckCircleOutline sx={styles.statusIcon} />;

                                    default:
                                      return '';
                                  }
                                })()}
                                <span style={styles.mainEllipsisText}>
                                  {((): string => {
                                    switch (data.ocr_status) {
                                      case ocrStatus.uploaded:
                                      case ocrStatus.processing:
                                        return '読取中';
                                      case ocrStatus.completed:
                                        return '読取完了';

                                      default:
                                        return '-';
                                    }
                                  })()}
                                </span>
                              </Box>
                            )}
                          </TableCell>
                          {/* ファイル名 */}
                          <TableCell sx={styles.tableDataText}>
                            {data?.fileName ? (
                              <Tooltip title={data?.fileName}>
                                <span style={styles.NameEllipsisText}>{data?.fileName}</span>
                              </Tooltip>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          {/* マンション名 */}
                          <TableCell sx={styles.tableDataText}>
                            {data.result_data?.mansionName?.value ? (
                              <Tooltip title={data.result_data.mansionName.value}>
                                <span style={styles.roomNumberEllipsisText}>
                                  {data.result_data.mansionName.value}
                                </span>
                              </Tooltip>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          {/* 部屋番号 */}
                          <TableCell sx={styles.tableDataText}>
                            {data.result_data?.roomNumber?.value ? (
                              <Tooltip title={data.result_data.roomNumber.value}>
                                <span style={styles.roomNumberEllipsisText}>
                                  {data.result_data.roomNumber.value}
                                </span>
                              </Tooltip>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          {/* 原因の設定年月日 */}
                          <TableCell sx={styles.tableDataText}>
                            {data.result_data?.establishmentDate?.value ? (
                              <Tooltip title={data.result_data.establishmentDate.value}>
                                <span style={styles.Invoice_numberEllipsisText}>
                                  {dayjs(data.result_data.establishmentDate.value).format(
                                    'YYYY/MM/DD',
                                  )}
                                </span>
                              </Tooltip>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          {/* 所有者(債務者)の住所 */}
                          <TableCell sx={styles.tableDataText}>
                            {data.result_data?.debtorAddress?.value ? (
                              <Tooltip title={data.result_data?.debtorAddress.value}>
                                <span style={styles.AddressEllipsisText}>
                                  {data.result_data?.debtorAddress.value}
                                </span>
                              </Tooltip>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          {/* 所有者(債務者)の氏名 */}
                          <TableCell sx={styles.tableDataText}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {data.result_data?.debtorName?.value ? (
                                <Tooltip title={data.result_data?.debtorName.value}>
                                  <span style={styles.NameEllipsisText}>
                                    {data.result_data?.debtorName.value}
                                  </span>
                                </Tooltip>
                              ) : (
                                '-'
                              )}
                              {data.is_corporate_owner && (
                                <Chip
                                  label="法人"
                                  size="small"
                                  sx={{
                                    fontSize: '10px',
                                    height: '18px',
                                    backgroundColor: '#bdbdbd',
                                    color: '#fff',
                                    flexShrink: 0,
                                    borderRadius: '4px',
                                  }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          {/* 抵当権者の会社名 */}
                          <TableCell sx={styles.tableDataText}>
                            {data.result_data?.mortgageeCompanyName?.value ? (
                              <Tooltip title={data.result_data?.mortgageeCompanyName.value}>
                                <span style={styles.NameEllipsisText}>
                                  {data.result_data?.mortgageeCompanyName.value}
                                </span>
                              </Tooltip>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          {/* グループ */}
                          <TableCell sx={styles.tableDataText}>
                            {data.groupId
                              ? (groups.find(g => g.groupId === data.groupId)?.name ?? '-')
                              : '-'}
                          </TableCell>
                          {/* 作成者 */}
                          <TableCell sx={styles.tableCellAvatar}>
                            {/* 仮で直接画像URL指定 */}
                            <Avatar src={data.iconUrl} sx={styles.avatar} />
                            <Typography>
                              <Tooltip title={data.showName}>
                                <span style={styles.showNameEllipsisText}>{data.showName}</span>
                              </Tooltip>
                            </Typography>
                          </TableCell>
                          <TableCell sx={styles.tableCellButton}>
                            <Button
                              onClick={(event): void => {
                                event.stopPropagation();
                                setDeleteTargetId(data.resultId);
                                setDeleteTargetIndex(index);
                                setDeleteModalOpen(true);
                              }}
                              startIcon={<DeleteOutline sx={styles.icon18} />}
                              sx={[
                                tertiaryButtonBackgroundColor,
                                styles.buttonSmall,
                                {
                                  whiteSpace: 'nowrap', //改行させない
                                  wordBreak: 'keep-all', //日本語の途中で改行しない
                                  overflow: 'hidden', //スクロールバーを出さない
                                  textOverflow: 'ellipsis', //幅を超えたら「...」に
                                },
                              ]}
                            >
                              削除
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </InfiniteScroll>
              </Box>
            ) : (
              <>
                <DescriptionIcon sx={styles.icon} />
                <Typography sx={styles.title}>{titleLabel}</Typography>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={styles.describtion}>{desctiptionLabel}</Typography>
                </Box>
              </>
            )}
          </Box>
        </Grid2>
      </Box>

      <CustomLoading loading={loading} />

      <CustomSnackbar
        autoHideDuration={6000}
        open={snackbarOpen}
        onClose={(): void => setSnackbarOpen(false)}
        text={errorText}
        infoText={infoText}
      />

      <Dialog open={uploadModalOpen} sx={styles.dialog} TransitionProps={{ timeout: 0 }}>
        <Box sx={styles.dialogHeader}>
          <Typography sx={styles.dialogTitle}>ファイルをアップロード</Typography>
          <IconButton
            onClick={(): void => setUploadModalOpen(false)}
            sx={styles.iconButton}
            disabled={loading}
          >
            <Close sx={styles.dialogCloseButton} />
          </IconButton>
        </Box>

        {watch('images') && watch('images').length > 0 && (
          <Box>
            {watch('images').map((image: File, index: number) => (
              <Chip
                key={index}
                label={image.name}
                onDelete={loading ? undefined : (): void => handleFilesDeleted(index)}
                sx={styles.uploadFileChip}
                variant="outlined"
              />
            ))}
          </Box>
        )}

        <Controller
          control={control}
          name="images"
          render={({ field }): JSX.Element => (
            <FileUploader
              helperText={errors.images?.message}
              onFilesSelected={(files: FileList | null): void =>
                handleFilesSelected(files, field.onChange)
              }
              fileCountOverText={fileCountOverText}
              disabled={loading}
            />
          )}
        />

        <Typography sx={regularSm}>
          PDF、PNG、JPG、JPEGファイルを選択できます。
          <br />
          一度にアップロードできるのは最大30ファイルまでです。
          <br />
          各ファイルのサイズは、7MBまでアップロード可能です。
        </Typography>

        {dailyLimit != null && (
          <Typography
            sx={{
              fontSize: '13px',
              color: remainingUploads === 0 ? red50 : gray80,
              mt: '8px',
            }}
          >
            {remainingUploads === 0
              ? '本日のアップロード上限に達しました。'
              : `本日の残りアップロード可能数: ${remainingUploads}/${dailyLimit}件`}
          </Typography>
        )}

        <Box sx={styles.dialogButtonWrapper}>
          <Button
            sx={styles.uploadCancelButton}
            disabled={loading}
            onClick={(): void => setUploadModalOpen(false)}
          >
            <Typography sx={styles.uploadCancelButtonText}>キャンセル</Typography>
          </Button>
          <Button
            sx={styles.uploadSubmitButton}
            disabled={loading || (dailyLimit != null && remainingUploads === 0)}
            onClick={async (): Promise<void> => {
              await handleSubmit(async () => onSubmit())();
            }}
          >
            <Typography sx={styles.uploadSubmitButtonText}>
              {loading ? 'アップロード中...' : 'アップロード'}
            </Typography>
          </Button>
        </Box>
      </Dialog>

      <DeleteModal
        open={deleteModalOpen}
        onClose={(): void => setDeleteModalOpen(false)}
        onDelete={handleDeleteResult}
        title="ファイル"
      />

      <FileExportDialog
        open={fileExportModalOpen}
        onClose={(): void => setFileExportModalOpen(false)}
        onExport={(fileType, encoding): void => {
          handleDownload(fileType, encoding as 'utf-8' | 'shift-jis');
        }}
      />
    </Box>
  );
};

export default AiOcr;
